import { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  RotateCcw, RefreshCw, Clock, CheckCircle, XCircle, Package,
  Search, Filter, Eye, X, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RETURN_STATUS = [
  { key: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-800' },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
  { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' }
];

export const AdminReturns = () => {
  const [returns, setReturns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadReturns();
  }, [statusFilter, typeFilter]);

  const loadReturns = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('return_type', typeFilter);
      
      const response = await axios.get(`${API}/admin/returns?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReturns(response.data.returns || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (returnReq) => {
    setSelectedReturn(returnReq);
    setNewStatus(returnReq.status);
    setAdminNotes(returnReq.admin_notes || '');
    setRefundAmount(returnReq.refund_amount?.toString() || '');
    setShowModal(true);
  };

  const updateReturn = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/returns/${selectedReturn.id}`, {
        status: newStatus,
        admin_notes: adminNotes || undefined,
        refund_amount: refundAmount ? parseFloat(refundAmount) : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Return request updated');
      setShowModal(false);
      loadReturns();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-returns">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-light text-charcoal">Returns & Exchanges</h1>
        <p className="text-charcoal-light text-sm mt-1">Manage customer return and exchange requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {RETURN_STATUS.map(status => (
          <button
            key={status.key}
            onClick={() => setStatusFilter(statusFilter === status.key ? '' : status.key)}
            className={`p-4 border text-center transition-colors ${
              statusFilter === status.key 
                ? 'border-gold bg-gold/5' 
                : 'border-charcoal/10 hover:border-gold/50'
            }`}
          >
            <p className="font-heading text-2xl font-light text-charcoal">{stats[status.key] || 0}</p>
            <p className="text-xs text-charcoal-light mt-1">{status.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-charcoal/10 p-4 flex flex-wrap gap-4 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
        >
          <option value="">All Status</option>
          {RETURN_STATUS.map(status => (
            <option key={status.key} value={status.key}>{status.label}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
        >
          <option value="">All Types</option>
          <option value="return">Return</option>
          <option value="exchange">Exchange</option>
        </select>
      </div>

      {/* Returns Table */}
      <div className="bg-white border border-charcoal/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige/50">
              <tr>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Request</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Order</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Customer</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Type</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Reason</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Status</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Date</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {returns.length > 0 ? (
                returns.map((returnReq) => {
                  const statusConfig = RETURN_STATUS.find(s => s.key === returnReq.status) || RETURN_STATUS[0];
                  return (
                    <tr key={returnReq.id} className="hover:bg-beige/30 transition-colors">
                      <td className="px-5 py-4">
                        <span className="text-sm font-medium text-charcoal">#{returnReq.id.slice(0, 8)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal">#{returnReq.order_number}</span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-charcoal">{returnReq.customer_name}</p>
                        <p className="text-xs text-charcoal-light">{returnReq.customer_phone}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-1 ${
                          returnReq.return_type === 'exchange' 
                            ? 'bg-purple-50 text-purple-700' 
                            : 'bg-orange-50 text-orange-700'
                        }`}>
                          {returnReq.return_type === 'exchange' ? (
                            <><RefreshCw className="w-3 h-3 inline mr-1" />Exchange</>
                          ) : (
                            <><RotateCcw className="w-3 h-3 inline mr-1" />Return</>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal line-clamp-1">{returnReq.reason}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-1 ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal-light">{formatDate(returnReq.created_at)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => openModal(returnReq)}
                          className="p-1.5 text-charcoal-light hover:text-gold hover:bg-beige transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-charcoal-light">
                    <RotateCcw className="w-8 h-8 mx-auto mb-3 opacity-50" />
                    <p>No return requests found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {showModal && selectedReturn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h2 className="font-heading text-xl font-light text-charcoal">
                {selectedReturn.return_type === 'exchange' ? 'Exchange' : 'Return'} Request
              </h2>
              <button onClick={() => setShowModal(false)} className="text-charcoal-light hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer & Order Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-charcoal-light">Customer:</span>
                  <p className="text-charcoal font-medium">{selectedReturn.customer_name}</p>
                  <p className="text-charcoal-light">{selectedReturn.customer_email}</p>
                  <p className="text-charcoal-light">{selectedReturn.customer_phone}</p>
                </div>
                <div>
                  <span className="text-charcoal-light">Order:</span>
                  <p className="text-charcoal font-medium">#{selectedReturn.order_number}</p>
                  <p className="text-charcoal-light">Requested: {formatDate(selectedReturn.created_at)}</p>
                </div>
              </div>

              {/* Reason */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">Reason</h4>
                <p className="text-sm text-charcoal bg-beige/50 p-3">{selectedReturn.reason}</p>
                {selectedReturn.additional_notes && (
                  <p className="text-sm text-charcoal-light mt-2">{selectedReturn.additional_notes}</p>
                )}
              </div>

              {/* Items */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">Items</h4>
                <div className="border border-charcoal/10 divide-y divide-charcoal/5">
                  {selectedReturn.items?.map((item, idx) => (
                    <div key={idx} className="p-3 flex items-center gap-4">
                      <Package className="w-8 h-8 text-charcoal-light" />
                      <div className="flex-1">
                        <p className="text-sm text-charcoal">{item.product_name}</p>
                        <p className="text-xs text-charcoal-light">Qty: {item.quantity} | Reason: {item.reason || selectedReturn.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Images */}
              {selectedReturn.images?.length > 0 && (
                <div>
                  <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">Attached Images</h4>
                  <div className="flex gap-2 flex-wrap">
                    {selectedReturn.images.map((img, idx) => (
                      <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                        <img src={img} alt={`Return image ${idx + 1}`} className="w-20 h-20 object-cover border border-charcoal/10" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Update Status */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">Update Status</h4>
                <div className="grid grid-cols-4 gap-2">
                  {RETURN_STATUS.map(status => (
                    <button
                      key={status.key}
                      onClick={() => setNewStatus(status.key)}
                      className={`p-2 border text-center text-sm transition-colors ${
                        newStatus === status.key 
                          ? 'border-gold bg-gold/10 text-gold' 
                          : 'border-charcoal/10 hover:border-gold/50 text-charcoal-light'
                      }`}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Refund Amount */}
              {selectedReturn.return_type === 'return' && (
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    Refund Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="w-full h-10 px-4 border border-charcoal/10 focus:border-gold outline-none"
                    placeholder="Enter refund amount"
                  />
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full h-24 px-4 py-2 border border-charcoal/10 focus:border-gold outline-none resize-none"
                  placeholder="Internal notes..."
                />
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={updateReturn}
                disabled={updating}
                className="px-6 py-2 bg-gold text-white text-sm disabled:opacity-50"
              >
                {updating ? 'Updating...' : 'Update Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReturns;
